import {
  KidTemplateIncompleteEntry,
  KidTemplateIncompleteEntryTemplate,
  KidTemplateOutputModel,
  KidTemplateSetupStatus,
  KidTemplateType,
} from '../../../../api/kids/types';
import { RaidrV2RoutePaths } from '../../../../routes/page-routes';
import {
  KID_TEMPLATE_TYPE_VALUE_SET,
  UNKNOWN_TEMPLATE_GROUP_KEY,
  normalizeTemplateSetupStatus,
} from './templateManagement.constants';

/** API may send enum member names (e.g. SUB_FUND) instead of values (sub_fund). */
const ENUM_NAME_TO_KID_TEMPLATE_TYPE: Record<string, KidTemplateType> = {
  MANCO: KidTemplateType.MANCO,
  SUB_FUND: KidTemplateType.SUB_FUND,
  FUND_MANAGER: KidTemplateType.FUND_MANAGER,
  CIU_FUND: KidTemplateType.CIU_FUND,
  ADMINISTRATOR: KidTemplateType.ADMINISTRATOR,
};

export function pickRawTemplateType(
  source: KidTemplateOutputModel | KidTemplateIncompleteEntryTemplate | undefined,
): unknown {
  if (!source || typeof source !== 'object') return undefined;
  const o = source as KidTemplateOutputModel & { templateType?: unknown };
  return o.template_type ?? o.templateType;
}

export function pickTemplateSetupStatus(
  source: KidTemplateOutputModel | KidTemplateIncompleteEntryTemplate | undefined,
): string | null {
  if (!source || typeof source !== 'object') return null;
  const o = source as KidTemplateOutputModel & {
    templateSetupStatus?: unknown;
    setup_status?: unknown;
    setupStatus?: unknown;
  };
  const raw =
    o.template_setup_status ?? o.templateSetupStatus ?? o.setup_status ?? o.setupStatus;
  return typeof raw === 'string' ? normalizeTemplateSetupStatus(raw) : null;
}

/**
 * Prefer live missing/invalid coverage over the template payload's setup status.
 * The list endpoint can keep `incomplete` after every document already has the template.
 */
export function resolveTemplateSetupStatusFromCoverage(options: {
  templateStatus: string | null;
  incompleteEntry?: KidTemplateIncompleteEntry;
  coverageKnown: boolean;
}): string | null {
  if (options.coverageKnown) {
    const invalidCount = options.incompleteEntry?.invalid_overviews?.length ?? 0;
    const missingCount = options.incompleteEntry?.missing_overviews?.length ?? 0;
    if (invalidCount > 0) return KidTemplateSetupStatus.INVALID;
    if (missingCount > 0) return KidTemplateSetupStatus.INCOMPLETE;
    return KidTemplateSetupStatus.COMPLETE;
  }
  return options.templateStatus;
}

export type TemplateSetupStatusPill = {
  status: string;
  count?: number;
};

/** Invalid and incomplete are independent: a template can need both pills. */
export function getTemplateSetupStatusPills(options: {
  setupStatus?: string | null;
  missingCount?: number;
  invalidCount?: number;
}): TemplateSetupStatusPill[] {
  const missingCount = options.missingCount ?? 0;
  const invalidCount = options.invalidCount ?? 0;
  const pills: TemplateSetupStatusPill[] = [];
  if (invalidCount > 0) {
    pills.push({ status: KidTemplateSetupStatus.INVALID, count: invalidCount });
  }
  if (missingCount > 0) {
    pills.push({
      status: KidTemplateSetupStatus.INCOMPLETE,
      count: missingCount,
    });
  }
  if (pills.length > 0) return pills;
  if (options.setupStatus) return [{ status: options.setupStatus }];
  return [];
}

/** Stable group key; list endpoint sometimes omits `template_type`. */
export function resolveTemplateGroupingTypeKey(
  template: KidTemplateOutputModel,
  incompleteFallback?: unknown,
): string {
  const raw = pickRawTemplateType(template) ?? incompleteFallback;
  if (raw === undefined || raw === null || raw === '') return UNKNOWN_TEMPLATE_GROUP_KEY;
  const s = String(raw).trim().toLowerCase().replace(/-/g, '_');
  if (KID_TEMPLATE_TYPE_VALUE_SET.has(s)) return s;
  const asUpperSnake = String(raw).trim().toUpperCase().replace(/-/g, '_');
  const fromEnumName = ENUM_NAME_TO_KID_TEMPLATE_TYPE[asUpperSnake];
  if (fromEnumName) return fromEnumName;
  return s;
}

export function buildTemplateEditPath(templateId: string): string {
  const params = new URLSearchParams({ templateId });
  return `${RaidrV2RoutePaths.TEMPLATE_EDIT}?${params.toString()}`;
}
